import React, { useEffect, useState } from 'react';
import SuperadminLayout from '../../components/SuperadminLayout';
import { listPaymentSubmissions, approvePaymentSubmission, rejectPaymentSubmission } from '../../api/superadmin';
import './SuperadminPaymentSubmissionsPage.css';

const statusLabels = {
  pending: 'En attente',
  approved: 'Approuvé',
  rejected: 'Rejeté',
};

const money = (n) => (n === null || n === undefined ? '—' : Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

// Revue des justificatifs de paiement deposes par les companies (page "Pay
// Online" -> PaymentInvoicePage.jsx) — demande explicite 2026-09-19 ("dans
// le superadmin on doit le lister pourque le super admin puisse le verifier
// et confirmer cette facture"). Approuver incremente invoice.amount_paid du
// montant VERIFIE (pre-rempli avec le montant declare par la company, mais
// modifiable) et recalcule automatiquement Company.billing_blocked.
const SuperadminPaymentSubmissionsPage = () => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('pending');
  const [busyId, setBusyId] = useState(null);
  const [amountDrafts, setAmountDrafts] = useState({});
  const [reviewingId, setReviewingId] = useState(null);

  const load = (filter) => {
    setLoading(true);
    setError('');
    listPaymentSubmissions(filter)
      .then((data) => {
        setSubmissions(data);
        const drafts = {};
        data.forEach((s) => { drafts[s.id] = s.amount === null ? '' : String(s.amount); });
        setAmountDrafts((prev) => ({ ...drafts, ...prev }));
      })
      .catch(() => setError('Impossible de charger les justificatifs.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(statusFilter); }, [statusFilter]);

  const handleApprove = async (submission) => {
    const value = amountDrafts[submission.id];
    if (value === '' || value === undefined || Number.isNaN(Number(value)) || Number(value) <= 0) {
      setError('Montant vérifié invalide.');
      return;
    }
    setBusyId(submission.id);
    setError('');
    try {
      const updated = await approvePaymentSubmission(submission.id, value);
      setSubmissions((prev) => prev.map((s) => (s.id === submission.id ? updated : s)));
      setReviewingId(null);
    } catch (err) {
      setError("Approbation impossible pour ce justificatif.");
    } finally {
      setBusyId(null);
    }
  };

  const handleReject = async (submission) => {
    setBusyId(submission.id);
    setError('');
    try {
      const updated = await rejectPaymentSubmission(submission.id);
      setSubmissions((prev) => prev.map((s) => (s.id === submission.id ? updated : s)));
    } catch (err) {
      setError('Rejet impossible pour ce justificatif.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <SuperadminLayout>
      <h2 className="sps-title">Paiements en ligne</h2>

      <div className="sps-filter-row">
        {['pending', 'approved', 'rejected', ''].map((f) => (
          <button
            key={f || 'all'}
            type="button"
            className={`sps-filter-btn ${statusFilter === f ? 'sps-filter-btn-active' : ''}`}
            onClick={() => setStatusFilter(f)}
          >
            {f ? statusLabels[f] : 'Tous'}
          </button>
        ))}
      </div>

      {error && <p className="sps-error">{error}</p>}

      {loading ? (
        <p>Chargement...</p>
      ) : (
        <div className="sps-table-wrap">
          <table className="sps-table">
            <thead>
              <tr>
                <th>Company</th>
                <th>Facture</th>
                <th>Méthode</th>
                <th>Montant déclaré</th>
                <th>Reçu</th>
                <th>Note</th>
                <th>Déposé le</th>
                <th>Statut</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {submissions.length === 0 && (
                <tr><td colSpan={9} className="sps-empty">Aucun justificatif.</td></tr>
              )}
              {submissions.map((s) => (
                <tr key={s.id}>
                  <td>{s.company_name}</td>
                  <td>{s.invoice_reference || '—'}</td>
                  <td>{s.method_label}</td>
                  <td className="sps-money">{money(s.amount)}</td>
                  <td>
                    <a href={s.receipt} target="_blank" rel="noreferrer">Voir</a>
                  </td>
                  <td>{s.note || '—'}</td>
                  <td>{new Date(s.submitted_at).toLocaleString()}</td>
                  <td>{statusLabels[s.status] || s.status}</td>
                  <td>
                    {s.status !== 'pending' ? (
                      '—'
                    ) : reviewingId === s.id ? (
                      <div className="sps-review-row">
                        <input
                          className="sps-amount-input"
                          type="text"
                          inputMode="decimal"
                          value={amountDrafts[s.id] ?? ''}
                          onChange={(e) => setAmountDrafts((prev) => ({ ...prev, [s.id]: e.target.value }))}
                        />
                        <button
                          className="sps-approve-btn"
                          disabled={busyId === s.id}
                          onClick={() => handleApprove(s)}
                        >
                          Confirmer
                        </button>
                        <button
                          className="sps-cancel-btn"
                          disabled={busyId === s.id}
                          onClick={() => setReviewingId(null)}
                        >
                          Annuler
                        </button>
                      </div>
                    ) : (
                      <div className="sps-actions">
                        <button
                          className="sps-approve-btn"
                          disabled={busyId === s.id}
                          onClick={() => setReviewingId(s.id)}
                        >
                          Approuver
                        </button>
                        <button
                          className="sps-reject-btn"
                          disabled={busyId === s.id}
                          onClick={() => handleReject(s)}
                        >
                          Rejeter
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </SuperadminLayout>
  );
};

export default SuperadminPaymentSubmissionsPage;
